import { SpeechAdapter } from './SpeechAdapter';

// ==============================|| AZURE COGNITIVE SERVICES SPEECH ADAPTER ||============================== //
//
// Uses Azure Cognitive Services Speech SDK for speech-to-text.
// Requires VITE_AZURE_SPEECH_KEY and VITE_AZURE_SPEECH_REGION env vars.
//

class AzureSpeechAdapter extends SpeechAdapter {
  constructor() {
    super();
    this._recognizer = null;
    this._onEndCallback = null;
    this._stopping = false;
  }

  isSupported() {
    return !!(import.meta.env.VITE_AZURE_SPEECH_KEY && import.meta.env.VITE_AZURE_SPEECH_REGION);
  }

  async _validateKey() {
    const key = import.meta.env.VITE_AZURE_SPEECH_KEY;
    const region = import.meta.env.VITE_AZURE_SPEECH_REGION;
    const url = `https://${region}.api.cognitive.microsoft.com/sts/v1.0/issueToken`;
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Ocp-Apim-Subscription-Key': key }
    });
    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Azure Speech key validation failed (HTTP ${res.status}): ${text}`);
    }
  }

  async start({ lang, continuous = true, interimResults = true, onResult, onError, onEnd, onStart }) {
    if (!this.isSupported()) {
      onError?.(new Error('Azure Speech credentials not configured'));
      return;
    }

    try {
      await this._validateKey();
    } catch (err) {
      onError?.(err);
      return;
    }

    await this.stop();
    this._stopping = false;
    this._onEndCallback = onEnd;

    const SpeechSDK = await import('microsoft-cognitiveservices-speech-sdk');

    const speechConfig = SpeechSDK.SpeechConfig.fromSubscription(
      import.meta.env.VITE_AZURE_SPEECH_KEY,
      import.meta.env.VITE_AZURE_SPEECH_REGION
    );
    speechConfig.speechRecognitionLanguage = lang;

    if (interimResults) {
      speechConfig.outputFormat = SpeechSDK.OutputFormat.Detailed;
    }

    const audioConfig = SpeechSDK.AudioConfig.fromDefaultMicrophoneInput();
    this._recognizer = new SpeechSDK.SpeechRecognizer(speechConfig, audioConfig);

    if (interimResults) {
      this._recognizer.recognizing = (_sender, event) => {
        const text = event.result.text;
        if (text) {
          onResult?.({ transcript: text, isFinal: false });
        }
      };
    }

    this._recognizer.recognized = (_sender, event) => {
      if (event.result.reason === SpeechSDK.ResultReason.RecognizedSpeech) {
        const text = event.result.text;
        if (text) {
          onResult?.({ transcript: text, isFinal: true });
        }
      }
    };

    this._recognizer.canceled = (_sender, event) => {
      if (this._stopping) return;
      if (event.reason === SpeechSDK.CancellationReason.Error) {
        onError?.(new Error(`Azure Speech error: ${event.errorDetails}`));
      }
    };

    this._recognizer.sessionStopped = () => {
      this._onEndCallback?.();
      this._recognizer = null;
    };

    if (continuous) {
      this._recognizer.startContinuousRecognitionAsync(
        () => onStart?.(),
        (err) => onError?.(new Error(err))
      );
    } else {
      this._recognizer.recognizeOnceAsync(
        (result) => {
          if (result.text) {
            onResult?.({ transcript: result.text, isFinal: true });
          }
          onEnd?.();
          this._recognizer = null;
        },
        (err) => onError?.(new Error(err))
      );
    }
  }

  stop() {
    return new Promise((resolve) => {
      if (!this._recognizer) {
        resolve();
        return;
      }
      this._stopping = true;
      this._recognizer.stopContinuousRecognitionAsync(
        () => {
          this._recognizer?.close();
          this._recognizer = null;
          resolve();
        },
        () => {
          this._recognizer = null;
          resolve();
        }
      );
    });
  }

  abort() {
    this._stopping = true;
    if (this._recognizer) {
      try {
        this._recognizer.close();
      } catch {
        // ignore
      }
      this._recognizer = null;
    }
  }
}

export default AzureSpeechAdapter;
